/**
 * Pure model builder for the Shape (S2): the miniature article skeleton as
 * an ORDERED element list, derived entirely from the recipe's clamped values
 * + floor booleans (S1 outputs). The React component (RecipeShape.tsx) is a
 * thin renderer over this model; all ordering/presence decisions live here.
 *
 * Canonical order (DESIGN-PROPOSAL §5.1, 11-element anatomy of the published
 * ground-truth export):
 *   title → TOC box → key takeaways → intro → H2 sections (with H3 ticks,
 *   ghost bars rendering the min–max range; images w/ captions + callouts
 *   interleaved in-flow per policy) → table → CTA → FAQ.
 * Byline and SEO-METADATA are NEVER emitted (export-only artifacts).
 *
 * Compact recipes yield deliberately-COMPLETE skeletons of their class
 * (CONSULTS §1): absent-by-design elements simply do not exist in the model
 * — a short-form Shape is a tight complete miniature, never a standard Shape
 * with holes or absence markers. (Absence signaling lives ONLY in the S4
 * detail pane's fixed-slot row — ruled tension resolution.)
 *
 * No planner/floor math: words/sections are the S1-clamped display values;
 * the per-section word figure is a display proportion of those values.
 */

import { GenerationRecipe, DEFAULT_RECIPE } from '../recipeTypes';
import { ShapeFloorPayload } from './shapeFloor';
import { ClampedRecipeShape, clampRecipeToFloor } from './clampToFloor';

export type ShapeElement =
  | { kind: 'title' }
  | { kind: 'toc' }
  | { kind: 'key-takeaways' }
  | { kind: 'intro' }
  | { kind: 'section'; index: number; words: number; h3: boolean; ghost: boolean }
  | { kind: 'list' }
  | { kind: 'image'; index: number; caption: boolean }
  | { kind: 'callout'; index: number }
  | { kind: 'table' }
  | { kind: 'cta' }
  | { kind: 'faq'; count: number };

export interface ShapeModel {
  elements: ShapeElement[];
  /** The clamped display values the model was built from (for captions/labels). */
  clamped: ClampedRecipeShape;
}

/** Display proportion: per-section word budget across the mid-range count. */
function perSectionWords(clamped: ClampedRecipeShape): number {
  const mid = Math.max(
    1,
    Math.round((clamped.minSections.value + clamped.maxSections.value) / 2),
  );
  return Math.max(1, Math.round(clamped.words.value / mid));
}

/**
 * Distribute `count` in-flow markers across `sections` solid section slots,
 * round-robin starting after section 1. Returns a map sectionIndex -> count.
 * Ghost (range) sections never receive markers — they depict the range,
 * not planned content.
 */
function distribute(count: number, sections: number): Map<number, number> {
  const map = new Map<number, number>();
  if (count <= 0 || sections <= 0) return map;
  for (let i = 0; i < count; i += 1) {
    const slot = (i % sections) + 1;
    map.set(slot, (map.get(slot) ?? 0) + 1);
  }
  return map;
}

export function buildShapeModel(
  recipe: GenerationRecipe,
  floor: ShapeFloorPayload | undefined,
): ShapeModel {
  const clamped = clampRecipeToFloor(recipe, floor);
  const { glyphs } = clamped;
  const elements: ShapeElement[] = [];

  // 1 — Title (byline never drawn — chrome, not structure).
  elements.push({ kind: 'title' });

  // 2 — Linked TOC box (one mark: presence, not census).
  if (glyphs.toc) elements.push({ kind: 'toc' });

  // 3 — Key takeaways.
  if (glyphs.keyTakeaways) elements.push({ kind: 'key-takeaways' });

  // 4 — Intro block.
  elements.push({ kind: 'intro' });

  // 5 — H2 sections: solid bars for the min, ghost bars for the min–max range.
  const solidCount = Math.max(1, clamped.minSections.value);
  const ghostCount = Math.max(0, clamped.maxSections.value - solidCount);
  const words = perSectionWords(clamped);

  // 6/7 — in-flow markers distributed across SOLID sections only.
  const imagePosition = recipe.imagePosition ?? DEFAULT_RECIPE.imagePosition;
  const imageCount = glyphs.image ? clamped.images.value : 0;
  const calloutCount = glyphs.callout ? clamped.callouts.value : 0;

  let imagesBySection = distribute(imageCount, solidCount);
  if (imagePosition === 'per-section') {
    // One image per section, capped at the image count.
    imagesBySection = distribute(Math.min(imageCount, solidCount), solidCount);
  }
  const calloutsBySection = distribute(calloutCount, solidCount);
  let listPlaced = false;

  const endImages: ShapeElement[] = [];

  for (let i = 1; i <= solidCount; i += 1) {
    elements.push({ kind: 'section', index: i, words, h3: clamped.h3Present, ghost: false });
    const calloutsHere = calloutsBySection.get(i) ?? 0;
    for (let c = 0; c < calloutsHere; c += 1) {
      elements.push({ kind: 'callout', index: c + 1 });
      // List mark rides with the first callout when present; otherwise it
      // anchors to the middle solid section (deterministic, in-flow).
      if (glyphs.list && !listPlaced) {
        elements.push({ kind: 'list' });
        listPlaced = true;
      }
    }
    if (imagePosition === 'after-intro' && i === 1) {
      // after-intro policy: the full image set follows the first section
      // (the intro-adjacent slot), none distributed later.
      for (let img = 0; img < imageCount; img += 1) {
        elements.push({ kind: 'image', index: img + 1, caption: true });
      }
    } else if (imagePosition === 'end-only') {
      // collected below, after the final solid section
    } else {
      const imagesHere = imagesBySection.get(i) ?? 0;
      for (let img = 0; img < imagesHere; img += 1) {
        elements.push({ kind: 'image', index: img + 1, caption: true });
      }
    }
    if (glyphs.list && !listPlaced && i === Math.ceil(solidCount / 2)) {
      elements.push({ kind: 'list' });
      listPlaced = true;
    }
  }
  if (imagePosition === 'end-only') {
    for (let img = 0; img < imageCount; img += 1) {
      endImages.push({ kind: 'image', index: img + 1, caption: true });
    }
  }
  elements.push(...endImages);

  // Ghost bars render the min–max range (§5.1) — after the solid flow.
  for (let g = 1; g <= ghostCount; g += 1) {
    elements.push({
      kind: 'section',
      index: solidCount + g,
      words,
      h3: clamped.h3Present,
      ghost: true,
    });
  }

  // 8 — Table (after the section flow; position-free profiles still render
  // presence here — the mark is presence, not placement).
  if (glyphs.table) elements.push({ kind: 'table' });

  // 9 — CTA (S2-C2: clamped presence = config OR hard floor; canonical
  // position before FAQ).
  if (clamped.ctaPresent) {
    elements.push({ kind: 'cta' });
  }

  // 10 — FAQ, always present (R7), count from the clamped FAQ axis.
  elements.push({ kind: 'faq', count: clamped.faqItems.value });

  // 11 — SEO METADATA: never emitted (export-only artifact).

  return { elements, clamped };
}
